import { forwardRef, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";

const inputClass =
  "mt-2 w-full rounded-xl border border-terakkiBorder bg-white px-4 py-3 text-terakkiInk placeholder-slate-400 transition-shadow duration-200 focus:border-terakkiBrand focus:outline-none focus:ring-2 focus:ring-terakkiBrand/40 disabled:opacity-60";

export const authLinkClass =
  "font-semibold text-terakkiBrandDeep hover:underline disabled:opacity-50";

/** Soft-themed labelled input used across every auth screen. */
export const AuthField = forwardRef(function AuthField(
  { id, label, error, className = "", ...rest },
  ref
) {
  return (
    <div>
      <label htmlFor={id} className="text-base font-medium text-terakkiInk">
        {label}
      </label>
      <input id={id} ref={ref} className={`${inputClass} ${className}`} {...rest} />
      {error && <p className="mt-1.5 text-sm text-terakkiError">{error}</p>}
    </div>
  );
});

/** Same field with a show/hide toggle for passwords. */
export const AuthPasswordField = forwardRef(function AuthPasswordField(
  { id, label, error, showLabel, hideLabel, disabled, ...rest },
  ref
) {
  const [visible, setVisible] = useState(false);

  return (
    <div>
      <label htmlFor={id} className="text-base font-medium text-terakkiInk">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          ref={ref}
          type={visible ? "text" : "password"}
          disabled={disabled}
          className={`${inputClass} pr-24`}
          {...rest}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          disabled={disabled}
          className="absolute inset-y-0 right-2 my-auto h-9 rounded-lg px-3 text-sm font-semibold text-terakkiBrandDeep transition-colors duration-200 hover:bg-terakkiBrandMist/70 disabled:opacity-50"
        >
          {visible ? hideLabel : showLabel}
        </button>
      </div>
      {error && <p className="mt-1.5 text-sm text-terakkiError">{error}</p>}
    </div>
  );
});

/** Primary gradient submit button. */
export function AuthSubmit({ loading, children, className = "", ...rest }) {
  return (
    <button
      type="submit"
      disabled={loading || rest.disabled}
      className={`mt-1 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-terakkiBrand to-terakkiBrandDeep py-3 font-semibold text-white shadow-lg shadow-terakkiBrand/30 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-terakkiBrand/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-terakkiBrand/50 active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 ${className}`}
      {...rest}
    >
      {loading && <CircularProgress size={18} color="inherit" />}
      {children}
    </button>
  );
}
